import React, { useState, useRef, useEffect } from 'react';
import { 
  Shield, Activity, Cpu, Database, Globe, Layers, 
  TrendingUp, Zap, Server, Radio, Satellite,
  BarChart3, Network, HardDrive, CloudLightning,
  Music, Play
} from 'lucide-react';

interface StreamMetrics {
  bandwidth: number;
  latency: number;
  packetLoss: number;
  fps: number;
  resolution: string;
  codec: string;
  bitrate: number;
  viewers: number;
  cpuUsage: number;
  memoryUsage: number;
  networkThroughput: number;
}

interface AIAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative';
  engagement: number;
  topics: string[];
  recommendations: string[];
}

const LiveStreamPro: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamMode, setStreamMode] = useState<'broadcast' | 'conference' | 'presentation'>('broadcast');
  const [metrics, setMetrics] = useState<StreamMetrics>({
    bandwidth: 9850,
    latency: 0.8,
    packetLoss: 0,
    fps: 100,
    resolution: '3840x2160',
    codec: 'H.265/HEVC',
    bitrate: 125,
    viewers: 0,
    cpuUsage: 12,
    memoryUsage: 28,
    networkThroughput: 125.4
  });
  
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis>({
    sentiment: 'positive',
    engagement: 92,
    topics: ['Technology', 'Innovation', 'Cloud Computing', 'AI/ML'],
    recommendations: [
      'Optimal encoding settings detected',
      'Network performance excellent',
      'Consider enabling HDR for enhanced quality',
      'AI-powered noise reduction available'
    ]
  });

  const [transcription, setTranscription] = useState<string[]>([]);
  const [networkNodes, setNetworkNodes] = useState<any[]>([]);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // Simulate real-time metrics
    const metricsInterval = setInterval(() => {
      if (isStreaming) {
        setMetrics(prev => ({
          ...prev,
          bandwidth: 9850 + Math.random() * 200 - 100,
          latency: 0.5 + Math.random() * 0.5,
          fps: 98 + Math.floor(Math.random() * 4),
          cpuUsage: 10 + Math.random() * 15,
          memoryUsage: 25 + Math.random() * 10,
          networkThroughput: 120 + Math.random() * 20,
          viewers: Math.min(prev.viewers + Math.floor(Math.random() * 3), 1000)
        }));
      }
    }, 1000);

    // Simulate network topology
    const nodes = [];
    for (let i = 0; i < 8; i++) {
      nodes.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        type: ['CDN', 'Edge', 'Origin', 'Cache'][Math.floor(Math.random() * 4)],
        load: Math.random() * 100
      });
    }
    setNetworkNodes(nodes);

    return () => clearInterval(metricsInterval);
  }, [isStreaming]);

  const startProfessionalStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          frameRate: { ideal: 100 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });

      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      setIsStreaming(true);
      
      // Initialize AI transcription
      simulateTranscription();
      
      stream.getTracks().forEach(track => {
        track.onended = () => stopStream();
      });
    } catch (err) {
      console.error('Stream initialization failed:', err);
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
    setMetrics(prev => ({ ...prev, viewers: 0 }));
  };

  const simulateTranscription = () => {
    const phrases = [
      "Initializing quantum-encrypted stream protocol...",
      "Neural network optimization at 98.7% efficiency",
      "Distributed edge computing nodes synchronized",
      "Real-time ML inference pipeline activated",
      "10Gb bandwidth allocation confirmed",
      "Advanced codec negotiation complete",
      "Multi-CDN failover system online"
    ];

    let index = 0;
    const transcriptionInterval = setInterval(() => {
      if (!isStreaming) {
        clearInterval(transcriptionInterval);
        return;
      }

      setTranscription(prev => [...prev.slice(-4), phrases[index % phrases.length]]);
      index++;
    }, 3000);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'HelloPanda') {
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('Mot de passe incorrect');
      setPassword('');
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-blue-500/30 p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Satellite className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Broadcast Studio</h2>
              <p className="text-gray-400">Accès protégé</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-blue-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Entrez le mot de passe"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Se connecter
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-6">
              Outil de streaming professionnel réservé
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950">
      {/* Professional Header */}
      <div className="border-b border-blue-900/30 bg-black/40 backdrop-blur-xl">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Satellite className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">InfinityCloud Broadcast Studio</h1>
                  <p className="text-xs text-blue-400">Enterprise-Grade Streaming Infrastructure</p>
                </div>
              </div>
              
              {isStreaming && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/50 rounded-lg">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 text-sm font-semibold">BROADCASTING</span>
                  </div>
                  <div className="text-sm text-gray-400">
                    Session ID: {Date.now().toString(36).toUpperCase()}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Network className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300">10Gb Ethernet</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-300">AES-256</span>
                </div>
                <div className="flex items-center gap-2">
                  <CloudLightning className="w-4 h-4 text-purple-400" />
                  <span className="text-gray-300">Edge Computing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Main Stream Display */}
          <div className="col-span-8 space-y-6">
            {/* Video Container */}
            <div className="relative bg-gradient-to-br from-slate-900/95 via-blue-950/95 to-black/95 rounded-2xl border-4 border-blue-500/50 overflow-hidden shadow-2xl">
              {!isStreaming ? (
                <div className="aspect-video flex items-center justify-center">
                  <div className="text-center space-y-6 p-8 relative z-10">
                    <div className="w-28 h-28 mx-auto rounded-3xl bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl border-4 border-white/20">
                      <Radio className="w-14 h-14 text-white" />
                    </div>
                    
                    <div>
                      <h2 className="text-4xl font-bold text-white mb-3">Professional Broadcasting Suite</h2>
                      <p className="text-gray-300 text-lg">10Gb Network • 100Hz Refresh • AI-Enhanced</p>
                    </div>
                    
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={() => setStreamMode('presentation')}
                        className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all border-4 ${
                          streamMode === 'presentation'
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white border-purple-400 shadow-lg shadow-purple-500/50 scale-105'
                            : 'bg-white/90 text-gray-800 border-gray-400 hover:bg-white hover:border-gray-500'
                        }`}
                      >
                        <svg className="w-6 h-6 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Présentation
                      </button>
                      
                      <button
                        onClick={() => setStreamMode('broadcast')}
                        className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all border-4 ${
                          streamMode === 'broadcast'
                            ? 'bg-gradient-to-r from-pink-600 to-orange-600 text-white border-pink-400 shadow-lg shadow-pink-500/50 scale-105'
                            : 'bg-white/90 text-gray-800 border-gray-400 hover:bg-white hover:border-gray-500'
                        }`}
                      >
                        <Music className="w-6 h-6 inline mr-2" />
                        Music Live
                      </button>
                    </div>
                    
                    <button
                      onClick={startProfessionalStream}
                      className="px-14 py-5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-bold text-xl hover:from-green-600 hover:to-emerald-600 transition-all transform hover:scale-105 shadow-2xl shadow-green-500/50 border-4 border-green-400"
                    >
                      <Play className="w-6 h-6 inline mr-3" />
                      {streamMode === 'presentation' ? 'Démarrer Présentation' : 'Démarrer Stream Musical'}
                    </button>
                    
                    <div className="text-sm text-gray-200 mt-4 bg-black/50 rounded-lg px-4 py-2">
                      {streamMode === 'presentation' 
                        ? "📊 Mode présentation pour partager votre écran avec un associé"
                        : "🎵 Mode streaming musical avec OBS pour diffusion live"
                      }
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <video 
                    ref={videoRef}
                    autoPlay 
                    muted 
                    className="w-full aspect-video bg-black"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Overlay Metrics */}
                  <div className="absolute top-4 left-4 space-y-2">
                    <div className="bg-black/80 backdrop-blur-md rounded-lg px-3 py-2 text-xs text-white font-mono">
                      {metrics.resolution} @ {metrics.fps}fps
                    </div>
                    <div className="bg-black/80 backdrop-blur-md rounded-lg px-3 py-2 text-xs text-white font-mono">
                      {metrics.codec} • {metrics.bitrate} Mbps
                    </div>
                  </div>
                  
                  <button
                    onClick={stopStream}
                    className="absolute top-4 right-4 px-6 py-2 bg-red-600/90 backdrop-blur-md text-white rounded-lg font-semibold hover:bg-red-700 transition-all"
                  >
                    Terminate Broadcast
                  </button>
                </>
              )}
            </div>

            {/* Real-time Metrics Dashboard */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Bandwidth', value: `${metrics.bandwidth.toFixed(0)} Mbps`, icon: Activity, color: 'from-blue-500 to-cyan-500', borderColor: 'border-blue-500' },
                { label: 'Latency', value: `${metrics.latency.toFixed(1)} ms`, icon: Zap, color: 'from-green-500 to-emerald-500', borderColor: 'border-green-500' },
                { label: 'CPU Load', value: `${metrics.cpuUsage.toFixed(0)}%`, icon: Cpu, color: 'from-purple-500 to-pink-500', borderColor: 'border-purple-500' },
                { label: 'Viewers', value: metrics.viewers.toLocaleString(), icon: Globe, color: 'from-orange-500 to-red-500', borderColor: 'border-orange-500' }
              ].map((metric, i) => (
                <div key={i} className={`bg-white/10 backdrop-blur-lg rounded-2xl border-3 ${metric.borderColor} p-4 shadow-xl hover:scale-105 transition-all`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">{metric.label}</span>
                    <metric.icon className={`w-4 h-4 text-${metric.color}-400`} />
                  </div>
                  <div className="text-2xl font-bold text-white font-mono">
                    {metric.value}
                  </div>
                  <div className="mt-2 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${metric.color} transition-all`}
                      style={{ width: `${Math.random() * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Control Panel */}
          <div className="col-span-4 space-y-6">
            {/* AI Analysis */}
            <div className="rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <CloudLightning className="w-5 h-5 text-white" />
                  AI Stream Intelligence
                </h3>
              </div>
              <div className="bg-white/5 backdrop-blur-sm p-6 border-x-4 border-b-4 border-purple-500/50">
                <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Engagement Score</span>
                    <span className="text-white font-semibold">{aiAnalysis.engagement}%</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all"
                      style={{ width: `${aiAnalysis.engagement}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-400 mb-2">Detected Topics</p>
                  <div className="flex flex-wrap gap-2">
                    {aiAnalysis.topics.map((topic, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-900/50 text-blue-300 text-xs rounded-lg">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-400 mb-2">AI Recommendations</p>
                  <div className="space-y-1">
                    {aiAnalysis.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1 h-1 bg-green-400 rounded-full mt-1.5" />
                        <p className="text-xs text-gray-300">{rec}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Network Topology */}
            <div className="rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Network className="w-5 h-5 text-white" />
                  CDN Distribution Network
                </h3>
              </div>
              <div className="bg-white/5 backdrop-blur-sm p-6 border-x-4 border-b-4 border-blue-500/50">
              
              <div className="relative h-48 bg-slate-900/50 rounded-lg">
                <svg className="absolute inset-0 w-full h-full">
                  {networkNodes.map((node, i) => (
                    <g key={i}>
                      <circle
                        cx={`${node.x}%`}
                        cy={`${node.y}%`}
                        r="8"
                        className={`fill-${node.load > 70 ? 'red' : node.load > 40 ? 'yellow' : 'green'}-500`}
                        opacity="0.8"
                      />
                      <text
                        x={`${node.x}%`}
                        y={`${node.y + 8}%`}
                        className="fill-gray-400 text-xs"
                        textAnchor="middle"
                      >
                        {node.type}
                      </text>
                    </g>
                  ))}
                  {networkNodes.map((node, i) => 
                    networkNodes.slice(i + 1).map((target, j) => (
                      <line
                        key={`${i}-${j}`}
                        x1={`${node.x}%`}
                        y1={`${node.y}%`}
                        x2={`${target.x}%`}
                        y2={`${target.y}%`}
                        stroke="rgba(59, 130, 246, 0.2)"
                        strokeWidth="1"
                      />
                    ))
                  )}
                </svg>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-gray-400">Optimal Load</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                  <span className="text-gray-400">Medium Load</span>
                </div>
              </div>
              </div>
            </div>

            {/* Live Transcription */}
            <div className="rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-white" />
                  Real-time Transcription
                </h3>
              </div>
              <div className="bg-white/5 backdrop-blur-sm p-6 border-x-4 border-b-4 border-green-500/50">
              
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {transcription.map((text, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-xs text-gray-500 font-mono">
                      {new Date().toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                    <p className="text-xs text-gray-300 flex-1">{text}</p>
                  </div>
                ))}
              </div>
              </div>
            </div>

            {/* Advanced Controls */}
            <div className="rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-white" />
                  Stream Configuration
                </h3>
              </div>
              <div className="bg-white/5 backdrop-blur-sm p-6 border-x-4 border-b-4 border-orange-500/50">
              
              <div className="space-y-3">
                <button className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-blue-800 transition-all">
                  Enable HDR10+ Enhancement
                </button>
                <button className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-purple-800 transition-all">
                  Activate AI Upscaling
                </button>
                <button className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm font-semibold hover:from-green-700 hover:to-green-800 transition-all">
                  Deploy Multi-CDN Failover
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreamPro;